import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {SignalValueLabelsInterface} from '@interfaces/trace'

export const toPaginatedResponse = <T>(
  response: BaseFetchResultsInterface<T> | T[],
): BaseFetchResultsInterface<T> =>
  Array.isArray(response)
    ? {
        count: response.length,
        next: null,
        previous: null,
        results: response,
      }
    : response

// One signal's value -> description and value -> colour maps, the shape every
// display consumer already keyed its labels by before they rode on the data.
export interface ValueLabelMaps {
  labels: Record<string, string>
  colors: Record<string, string>
}

/**
 * Split a signals/values/ entry's `labels` (AB#6168) into the description and
 * colour maps. Absent `labels` (a backend from before the field) is simply no
 * labels, so the charts keep working with raw values.
 */
export const toValueLabelMaps = (wire: SignalValueLabelsInterface | undefined): ValueLabelMaps => {
  const labels: Record<string, string> = {}
  const colors: Record<string, string> = {}
  for (const [value, entry] of Object.entries(wire ?? {})) {
    labels[value] = entry.description
    if (entry.color) {
      colors[value] = entry.color
    }
  }
  return {labels, colors}
}

/**
 * Fold one entry's labels into per-signal maps. Merged, not assigned: each
 * response labels only the values IT returned, so a signal read by more than
 * one request keeps the labels of all of them.
 */
export const addValueLabels = (
  labelsBySignal: Record<string, Record<string, string>>,
  colorsBySignal: Record<string, Record<string, string>>,
  signalId: string,
  wire: SignalValueLabelsInterface | undefined,
): void => {
  const {labels, colors} = toValueLabelMaps(wire)
  labelsBySignal[signalId] = {...labelsBySignal[signalId], ...labels}
  colorsBySignal[signalId] = {...colorsBySignal[signalId], ...colors}
}
